import { Location } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import { AnnounceDialogComponent } from '../../modalDialogs/announceDialog/announceDialog.component';
import { BottomSheetHelperService } from '../../services/bottomSheetHelper.service';
import { CoreCOMService } from '../../services/com.service';
import { CoreLanguageService } from '../../services/language.service';
import { CoreLogService } from '../../services/log.service';
import { snackBarMessageKey, SnackBarService } from '../../services/snackBar.service';
import { AnounceModalResult, DeliveryAddressType, OrderDataInputMaxLength, SnackBarMessageType } from '../../shared/contentTypes';
import { UserAddressViewModel } from '../../shared/models/userInfo.model';
import { CustomerInputFieldViewModel } from '../../shared/models/webshop.model';
import { OverlaySpinnerService } from '../overlayspinner/overlaySpiner.service';
import { UserAddressService } from './userAddress.service';
import { DynamicFormFieldsComponent } from '../../directives';

@Component({
  selector: 'ya-user-address',
  templateUrl: './userAddress.component.html',
  styleUrls: ['./userAddress.component.scss'],
  providers: [OverlaySpinnerService, UserAddressService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserAddressComponent implements OnInit, OnDestroy, AfterViewInit {

  public addressInputsViewModel: CustomerInputFieldViewModel[];
  public deliveryAddressType: DeliveryAddressType;
  public AddressType = DeliveryAddressType;
  private addressId: string;
  public orderDataInputMaxLength = OrderDataInputMaxLength;
  public isOnline: boolean;
  public userAddressViewModel: UserAddressViewModel = new UserAddressViewModel();;

  private _langSubscriber: Subscription;
  private _onlineSupscription: Subscription;
  private _offlineSupscription: Subscription;

  @ViewChild("dynamicFormFieldsComponent") dynamicFormFieldsComponent: DynamicFormFieldsComponent;

  constructor(private _activatedRoute: ActivatedRoute
    , private _userAddressService: UserAddressService
    , private _changeDetectorRef: ChangeDetectorRef
    , private _internationalisationService: InternationalisationService
    , private _coreLogService: CoreLogService
    , private _bottomSheetHelperService: BottomSheetHelperService
    , private _coreCOMService: CoreCOMService
    , private _coreLanguageService: CoreLanguageService
    , private _snackBarService: SnackBarService
    , private _location: Location
    , private _overlaySpinnerService: OverlaySpinnerService) { }

  ngAfterViewInit(): void { }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("UserAddressComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me.getRouteData();
    me.reloadDataAndRender();

    me.isOnline = me._coreCOMService.environment.networkInformation.isOnline;
    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    me._onlineSupscription = me._coreCOMService.environment.events.online.subscribe(() => {
      me.isOnline = true;
      me.PushChanges();
    });

    me._offlineSupscription = me._coreCOMService.environment.events.offline.subscribe(() => {
      me.isOnline = false;
      me.PushChanges();
    });

    me._userAddressService.webshopSettingsDataModelObserveChanges.subscribe(changes => {
      me.reloadDataAndRender();
    });
  }

  private getRouteData() {
    const me = this;
    me.deliveryAddressType = +me._activatedRoute.snapshot.params.addresType;
    if (me.deliveryAddressType === DeliveryAddressType.OldDeliveryAddress) {
      me.addressId = me._activatedRoute.snapshot.params.addressId;
    }
  }

  public getLocalPalceHolder(fieldName: string, isRequired: boolean = false) {
    const me = this;
    const OrderDataInput = `phOrderDataInputs_${fieldName}`;
    return `${me._coreLanguageService.value(OrderDataInput)}${isRequired ? ' *' : ''}`;
  }

  private reloadDataAndRender() {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._userAddressService.getAddressInputFields()
      .then(addressinputFields => {
        me.addressInputsViewModel = addressinputFields;
        return me.getAddressData();
      })
      .then(() => {
        me.PushChanges();
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(error => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._coreLogService.error("UserAddressComponent.reloadDataAndRender", "_userAddressService.getAddressInputFields", error)
      })
  }

  private getAddressData(): Promise<void> {
    const me = this;
    if (me.deliveryAddressType === DeliveryAddressType.OldDeliveryAddress) {
      return me._userAddressService.GetUserDeliveryAddressByToken(me.addressId)
        .then(billingInfo => {
          me.userAddressViewModel = billingInfo;
        })
        .catch(error => {
          me._coreLogService.error("UserAddressComponent.getAddressData", "_userAddressService.GetUserDeliveryAddressByToken", error)
          me._snackBarService.ShowErrorMessageFailed(83);
        });
    }
    return Promise.resolve(); // For new delivery address (no data to load)
  }

  public saveUserInfo() {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();

    if (me.dynamicFormFieldsComponent) {
      const formData = me.dynamicFormFieldsComponent.getDynamicFieldsInput();
      Object.assign(me.userAddressViewModel, formData);
    }
    me._userAddressService.saveCustomerDeliveryAddressDataModel(me.userAddressViewModel)
      .then(() => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
        me.PushChanges();
        if (me.deliveryAddressType === DeliveryAddressType.NewDeliveryAddress) {
          me._location.back();
        }
      })
      .catch(error => {
        me._coreLogService.error("UserAddressComponent.saveUserInfo", "_userAddressService.saveCustomerDeliveryAddressDataModel", error)
        me._snackBarService.ShowErrorMessageFailed(84);
        me._overlaySpinnerService.HideLayoutSpinner();
      })
  }


  /** Returns the initial user address data used to fill the form.*/
  public getUserInfoInitialData() {
    const me = this;
    return me.userAddressViewModel;
  }

  /**
   * Triggered whenever the form status changes.
   * Propagates the updated state to any listeners or parent components.
   */
  public onFormStatusChange(): void {
    this.PushChanges();
  }

  /** Indicates whether the dynamic form group exists.*/
  private get hasDynamicFormGroup(): boolean {
    const me = this;
    return !!(me.dynamicFormFieldsComponent && me.dynamicFormFieldsComponent.formGroup);
  }

  /** Checks whether the dynamic form group is present and currently valid.*/
  public isDynamicFormGroupValid(): boolean {
    const me = this;
    return me.hasDynamicFormGroup && me.dynamicFormFieldsComponent.formGroup.valid;
  }

  /** Marks all controls within the dynamic form group as touched. */
  public markAllFieldsAsTouched(): void {
    const me = this;
    if (me.dynamicFormFieldsComponent) {
      me.dynamicFormFieldsComponent.markAllFieldsAsTouched();
    }
  }

  /**open a modal dialog tobe sure that user want to delete a delivery address */
  public deleteAddress(): void {
    const me = this;
    me.openAnnounceDialog();
  }

  /**open modal to Anounce user for deleting a delivery */
  private openAnnounceDialog() {
    const me = this;
    const warningDialog = me._bottomSheetHelperService.open(AnnounceDialogComponent, {
      data: {
        title: "phUserProfileAddress_Announce_Dialog_Title"
        , content: "phUserProfileAddress_Announce_Dialog_Content"
        , firstBtnLabel: "phUserProfileAddress_Announce_Dialog_FirestBtn_Label"
        , secondBtnLabel: "phUserProfileAddress_Announce_Dialog_SecondBtn_Label"
      },
      panelClass: "mat-bottom-sheet-container--full-height"
    });
    warningDialog.afterDismissed().subscribe((result: AnounceModalResult) => {
      if (result === AnounceModalResult.FirstButton) {
        me._overlaySpinnerService.ShowLayoutSpinner();
        me._userAddressService.RemoveDeliveryAddress(me.userAddressViewModel.id)
          .then(() => {
            me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
            me._overlaySpinnerService.HideLayoutSpinner();
            me._location.back();
          })
          .catch(error => {
            me._coreLogService.error("UserAddressComponent.openAnnounceDialog", "RemoveDeliveryAddress", error)
            me._snackBarService.ShowErrorMessageFailed(85);
            me._overlaySpinnerService.HideLayoutSpinner();
          })
      }
    });
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
    if (me._onlineSupscription) {
      me._onlineSupscription.unsubscribe();
    }
    if (me._offlineSupscription) {
      me._offlineSupscription.unsubscribe();
    }
  }
}
